import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { Send, Search, X, Plus, MoreVertical, ArrowLeft, Paperclip, User, MessageSquare, Info, Bell, Check, Calendar, FileText, Clock, ExternalLink, Download, Trash2, AlertTriangle, Users, GraduationCap, Shield, ChevronDown, Filter, BarChart } from 'lucide-react';
// Message Types
type MessageType = 'text' | 'image' | 'file' | 'notification' | 'assignment' | 'grade' | 'meeting';
// Message Interface - Teacher Focused
interface TeacherMessage {
  id: string;
  sender: 'teacher' | 'student' | 'parent' | 'admin' | 'system';
  content: string;
  timestamp: string;
  type?: MessageType;
  attachments?: {
    id: string;
    name: string;
    type: 'image' | 'document' | 'video';
    url: string;
    size?: string;
  }[];
  read?: boolean;
  assignmentData?: {
    id: number;
    title: string;
    description: string;
    subject: string;
    dueDate: string;
    className: string;
    studentCount: number;
  };
  gradeData?: {
    studentName: string;
    subject: string;
    examName: string;
    grade: number;
    totalPoints: number;
    date: string;
  };
}
// Conversation Interface - Teacher Focused
interface TeacherConversation {
  id: string;
  type: 'student' | 'parent' | 'teacher' | 'admin' | 'support' | 'class_group';
  title: string;
  preview: string;
  status?: 'active' | 'archived' | 'urgent' | 'pending';
  date: string;
  unread: boolean;
  recipient: {
    id: string;
    name: string;
    role?: string;
    avatar?: string;
    className?: string; // For student/parent
    subject?: string; // For teacher
  };
  participants?: {
    id: string;
    name: string;
    avatar?: string;
  }[];
  messages: TeacherMessage[];
}
// Emerald Theme Color System
const teacherThemeColors = {
  // Border and main colors
  mainGradient: 'from-emerald-600 to-teal-600',
  borderGradient: 'from-emerald-600 to-teal-600',
  // Background colors
  cardBg: 'bg-black/90',
  hoverBg: 'hover:bg-emerald-900/20',
  activeBg: 'bg-gradient-to-r from-emerald-900/40 to-teal-900/40',
  // Border colors
  border: 'border-emerald-900/30',
  activeBorder: 'border-emerald-500/30',
  focusBorder: 'focus:ring-emerald-500/50',
  // Text colors
  primaryText: 'text-emerald-400',
  secondaryText: 'text-gray-400',
  // Button colors
  primaryButton: 'bg-gradient-to-r from-emerald-600 to-teal-600',
  secondaryButton: 'bg-emerald-900/20',
  // Status colors
  activeStatus: 'bg-emerald-900/20 text-emerald-400',
  urgentStatus: 'bg-red-900/20 text-red-400',
  pendingStatus: 'bg-yellow-900/20 text-yellow-400'
};
// Teacher recipient list
const teacherRecipients = [{
  id: 'student-1',
  name: 'Ahmet Yılmaz',
  role: '8-A Öğrencisi',
  className: '8-A',
  avatar: null,
  type: 'student'
}, {
  id: 'student-2',
  name: 'Ayşe Demir',
  role: '8-B Öğrencisi',
  className: '8-B',
  avatar: null,
  type: 'student'
}, {
  id: 'parent-1',
  name: 'Mehmet Yılmaz',
  role: 'Ahmet Yılmaz Velisi',
  className: '8-A',
  avatar: null,
  type: 'parent'
}, {
  id: 'parent-2',
  name: 'Fatma Demir',
  role: 'Ayşe Demir Velisi',
  className: '8-B',
  avatar: null,
  type: 'parent'
}, {
  id: 'teacher-1',
  name: 'Zeynep Öğretmen',
  role: 'Türkçe Öğretmeni',
  subject: 'Türkçe',
  avatar: null,
  type: 'teacher'
}, {
  id: 'teacher-2',
  name: 'Ali Öğretmen',
  role: 'Fen Bilimleri Öğretmeni',
  subject: 'Fen Bilimleri',
  avatar: null,
  type: 'teacher'
}, {
  id: 'admin-1',
  name: 'Okul Müdürü',
  role: 'Yönetim',
  avatar: null,
  type: 'admin'
}, {
  id: 'admin-2',
  name: 'Müdür Yardımcısı',
  role: 'Yönetim',
  avatar: null,
  type: 'admin'
}, {
  id: 'support',
  name: 'Teknik Destek',
  role: 'Yardım Masası',
  avatar: null,
  type: 'support'
}, {
  id: 'guidance',
  name: 'Rehberlik Servisi',
  role: 'Rehberlik Öğretmeni',
  avatar: null,
  type: 'support'
}];
// Technical support conversation data
const technicalSupportConversation: TeacherConversation = {
  id: 'support-001',
  type: 'support',
  title: 'Teknik Destek',
  preview: 'Giriş sorunu yaşıyorum, yardımcı olur musunuz?',
  status: 'active',
  date: '2 saat önce',
  unread: false,
  recipient: {
    id: 'support',
    name: 'Teknik Destek',
    role: 'Yardım Masası',
    avatar: null
  },
  messages: [{
    id: 'msg-support-1',
    sender: 'system',
    content: 'Teknik Destek ile konuşmanız başladı. Sorununuzu detaylı bir şekilde açıklayabilirsiniz.',
    timestamp: 'Bugün, 14:30',
    type: 'notification'
  }, {
    id: 'msg-support-2',
    sender: 'teacher',
    content: 'Merhaba, öğrenci notlarını girerken sistem donuyor. Sürekli yenilenmek zorunda kalıyorum.',
    timestamp: 'Bugün, 14:32',
    type: 'text'
  }, {
    id: 'msg-support-3',
    sender: 'admin',
    content: 'Merhaba! Sorununuz için teşekkürler. Tarayıcınızın önbelleğini temizlemeyi denediniz mi? Ayrıca hangi tarayıcı kullanıyorsunuz?',
    timestamp: 'Bugün, 14:45',
    type: 'text'
  }, {
    id: 'msg-support-4',
    sender: 'teacher',
    content: 'Chrome kullanıyorum. Önbellek temizledim ama sorun devam ediyor. Ekran görüntüsü göndereyim mi?',
    timestamp: 'Bugün, 14:47',
    type: 'text'
  }, {
    id: 'msg-support-5',
    sender: 'admin',
    content: 'Evet, ekran görüntüsü çok yardımcı olur. Ayrıca hangi sınıflarda bu sorunla karşılaştığınızı da belirtirseniz sevinirim.',
    timestamp: 'Bugün, 14:50',
    type: 'text'
  }]
};
// System notification conversation data (read-only)
const systemNotificationConversation: TeacherConversation = {
  id: 'system-001',
  type: 'support',
  title: 'Sistem Bildirimleri',
  preview: 'Yeni güncelleme duyurusu: Öğrenci rapor sistemi geliştirildi',
  status: 'active',
  date: '1 gün önce',
  unread: true,
  recipient: {
    id: 'system',
    name: 'Sistem Bildirimleri',
    role: 'Otomatik Bildirimler',
    avatar: null
  },
  messages: [{
    id: 'msg-system-1',
    sender: 'system',
    content: '🎉 Sistem Güncellemesi: Öğrenci analiz raporları artık daha detaylı! Yeni özellikler için Raporlar sekmesini ziyaret edebilirsiniz.',
    timestamp: 'Dün, 09:00',
    type: 'notification'
  }, {
    id: 'msg-system-2',
    sender: 'system',
    content: 'Scheduled maintenance will be performed on the system between 02:00 and 04:00 on 15 December 2024. During this time, you may not be able to access the system.',
    timestamp: 'Dün, 12:30',
    type: 'notification'
  }, {
    id: 'msg-system-3',
    sender: 'system',
    content: '📊 Yeni Özellik: Sınıf bazlı karşılaştırma grafikleri eklendi! Artık sınıflarınızın performansını daha kolay karşılaştırabilirsiniz.',
    timestamp: 'Dün, 16:45',
    type: 'notification'
  }, {
    id: 'msg-system-4',
    sender: 'system',
    content: '🔔 Hatırlatma: Dönem sonu not girişleri için son 5 gün! Lütfen öğrenci notlarınızı kontrol edip eksikleri tamamlayınız.',
    timestamp: 'Bugün, 08:00',
    type: 'notification'
  }]
};
// Sample conversation data
const sampleConversations: TeacherConversation[] = [{
  id: 'system-001',
  type: 'support',
  title: 'Sistem Bildirimleri',
  preview: 'Yeni güncelleme duyurusu: Öğrenci rapor sistemi geliştirildi',
  status: 'active',
  date: '1 gün önce',
  unread: true,
  recipient: {
    id: 'system',
    name: 'Sistem Bildirimleri',
    role: 'Otomatik Bildirimler',
    avatar: null
  },
  messages: [...systemNotificationConversation.messages]
}, {
  id: 'support-001',
  type: 'support',
  title: 'Teknik Destek',
  preview: 'Ekran görüntüsü gönderildi - not girme sorunu için',
  status: 'active',
  date: '2 saat önce',
  unread: false,
  recipient: {
    id: 'support',
    name: 'Teknik Destek',
    role: 'Yardım Masası',
    avatar: null
  },
  messages: [...technicalSupportConversation.messages]
}, {
  id: 'conv-1',
  type: 'student',
  title: 'Ahmet Yılmaz',
  preview: 'Öğretmenim, ödev hakkında sorum var',
  status: 'active',
  date: '14:30',
  unread: true,
  recipient: {
    id: 'student-1',
    name: 'Ahmet Yılmaz',
    role: '8-A Öğrencisi',
    className: '8-A',
    avatar: null
  },
  messages: [{
    id: 'msg-1',
    sender: 'student',
    content: 'Öğretmenim, ödev hakkında bir sorum var.',
    timestamp: '14:25'
  }, {
    id: 'msg-2',
    sender: 'student',
    content: 'Kareköklü sayılar konusundaki 5. soruyu anlayamadım.',
    timestamp: '14:27'
  }, {
    id: 'msg-3',
    sender: 'student',
    content: 'Yardımcı olabilir misiniz?',
    timestamp: '14:30'
  }]
}, {
  id: 'conv-2',
  type: 'parent',
  title: 'Mehmet Yılmaz',
  preview: "I want to learn about Ahmet's arrival, his arrival, about his arrival, about his arrival, about his arrival.",
  status: 'pending',
  date: '12:15',
  unread: false,
  recipient: {
    id: 'parent-1',
    name: 'Mehmet Yılmaz',
    role: 'Ahmet Yılmaz Velisi',
    className: '8-A',
    avatar: null
  },
  messages: [{
    id: 'msg-4',
    sender: 'parent',
    content: "Hello teacher, I want to learn about Ahmet's arrival, about his arrival, his arrival, about his arrival, about his arrival, ..., '', timestamp, '12:10"
  }, {
    id: 'msg-5',
    sender: 'teacher',
    content: 'Merhaba, tabii ki. Veli toplantısında ya da uygun olduğunuz bir zamanda görüşebiliriz.',
    timestamp: '12:15'
  }]
}, {
  id: 'conv-3',
  type: 'admin',
  title: 'Okul Müdürü',
  preview: 'Yarınki zümre toplantısı hk.',
  status: 'urgent',
  date: 'Dün',
  unread: true,
  recipient: {
    id: 'admin-1',
    name: 'Okul Müdürü',
    role: 'Yönetim',
    avatar: null
  },
  messages: [{
    id: 'msg-6',
    sender: 'admin',
    content: 'Merhaba, yarınki zümre toplantısı için hazırlık yapmanızı rica ediyorum.',
    timestamp: 'Dün 16:45'
  }, {
    id: 'msg-7',
    sender: 'admin',
    content: 'Sınav sonuçları ve öğrenci gelişim raporlarını getirmenizi rica ederim.',
    timestamp: 'Dün 16:47'
  }]
}, {
  id: 'conv-4',
  type: 'teacher',
  title: 'Zeynep Öğretmen',
  preview: 'Proje haftası için işbirliği yapalım mı?',
  status: 'active',
  date: '2 gün önce',
  unread: false,
  recipient: {
    id: 'teacher-1',
    name: 'Zeynep Öğretmen',
    role: 'Türkçe Öğretmeni',
    subject: 'Türkçe',
    avatar: null
  },
  messages: [{
    id: 'msg-8',
    sender: 'teacher',
    content: 'Merhaba, proje haftası için Matematik ve Türkçe derslerini birleştiren bir etkinlik düzenleyebilir miyiz?',
    timestamp: '2 gün önce 10:30'
  }, {
    id: 'msg-9',
    sender: 'teacher',
    content: 'Öğrencilerin hem matematiksel düşünme hem de ifade becerilerini geliştirecek bir proje olabilir.',
    timestamp: '2 gün önce 10:32'
  }]
}, {
  id: 'conv-5',
  type: 'class_group',
  title: '8-A Sınıfı',
  preview: 'Ödev duyurusu: Kareköklü sayılar',
  date: '3 gün önce',
  unread: false,
  recipient: {
    id: 'class-8a',
    name: '8-A Sınıfı',
    className: '8-A',
    role: 'Sınıf Grubu',
    avatar: null
  },
  participants: [{
    id: 'teacher-math',
    name: 'Matematik Öğretmeni'
  }, {
    id: 'student-1',
    name: 'Ahmet Yılmaz'
  }, {
    id: 'student-3',
    name: 'Ayşe Kaya'
  }],
  messages: [{
    id: 'msg-10',
    sender: 'teacher',
    type: 'assignment',
    content: '',
    timestamp: '3 gün önce 15:20',
    assignmentData: {
      id: 101,
      title: 'Kareköklü Sayılar Ödevi',
      description: 'Kitabınızdaki sayfa 45-47 arası tüm soruları çözün. Özellikle köklü sayılarda çarpma ve bölme işlemlerine dikkat edin.',
      subject: 'Matematik',
      dueDate: '10.12.2023',
      className: '8-A',
      studentCount: 28
    }
  }]
}];
// Image Modal Component
function ImageModal({
  imageUrl,
  onClose
}: {
  imageUrl: string;
  onClose: () => void;
}) {
  return <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50" onClick={onClose} data-id="element-2185">
      <div className="relative max-w-[90vw] max-h-[90vh]" data-id="element-2186">
        <img src={imageUrl} alt="Büyük görsel" className="w-full h-full object-contain" data-id="element-2187" />
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors" data-id="element-2188">
          <X className="w-6 h-6" data-id="element-2189" />
        </button>
      </div>
    </div>;
}
// Image Attachment Component
function TeacherImageAttachment({
  attachments,
  setAttachments,
  fileInputRef
}: {
  attachments: File[];
  setAttachments: React.Dispatch<React.SetStateAction<File[]>>;
  fileInputRef: React.RefObject<HTMLInputElement>;
}) {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const fileArray = Array.from(e.target.files);
      const validFiles = fileArray.filter(file => {
        // Sadece görsel ve dokuman dosyalarını kabul et
        return file.type.startsWith('image/') || file.type === 'application/pdf' || file.type.includes('document');
      });
      setAttachments([...attachments, ...validFiles]);
    }
  };
  const removeAttachment = (index: number) => {
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
  };
  const getFilePreview = (file: File) => {
    if (file.type.startsWith('image/')) {
      return URL.createObjectURL(file);
    }
    return null;
  };
  return <>
      <input type="file" ref={fileInputRef} onChange={handleFileChange} className="hidden" multiple accept="image/*,application/pdf,.doc,.docx,.txt" data-id="element-2190" />
      {/* Eklenen dosyaların önizlemesi */}
      {attachments.length > 0 && <div className="mb-3 flex flex-wrap gap-3" data-id="element-2191">
          {attachments.map((file, index) => <div key={index} className="relative group" data-id="element-2192">
              {file.type.startsWith('image/') ?
        // Görsel önizleme
        <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-emerald-900/20 border border-emerald-500/30" data-id="element-2193">
                  <img src={getFilePreview(file)} alt={file.name} className="w-full h-full object-cover" data-id="element-2194" />
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center" data-id="element-2195">
                    <button onClick={() => removeAttachment(index)} className="p-1 rounded-full bg-red-500 hover:bg-red-600 transition-colors" data-id="element-2196">
                      <X className="w-3 h-3 text-white" data-id="element-2197" />
                    </button>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-1 truncate" data-id="element-2198">
                    {file.name}
                  </div>
                </div> :
        // Dosya önizleme
        <div className="relative flex items-center gap-2 bg-emerald-900/20 text-white rounded-lg px-3 py-2 border border-emerald-500/30 min-w-[120px]" data-id="element-2199">
                  <FileText className="w-4 h-4 text-emerald-400" data-id="element-2200" />
                  <span className="text-xs truncate max-w-[80px]" data-id="element-2201">
                    {file.name}
                  </span>
                  <button onClick={() => removeAttachment(index)} className="text-gray-400 hover:text-white ml-auto" data-id="element-2202">
                    <X className="w-3 h-3" data-id="element-2203" />
                  </button>
                </div>}
            </div>)}
        </div>}
    </>;
}
// Image Message Bubble Component
function ImageMessageBubble({
  message,
  isCurrentUser
}: {
  message: TeacherMessage;
  isCurrentUser: boolean;
}) {
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');
  const openImageModal = (imageUrl: string) => {
    setSelectedImageUrl(imageUrl);
    setShowImageModal(true);
  };
  const downloadAttachment = (attachment: any) => {
    const link = document.createElement('a');
    link.href = attachment.url;
    link.download = attachment.name;
    link.click();
  };
  return <>
      <div className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} mb-4`} data-id="element-2204">
        <div className={`relative max-w-[80%] ${isCurrentUser ? 'bg-gradient-to-r from-emerald-600 to-teal-600 rounded-l-lg rounded-tr-lg shadow-[0_0_10px_rgba(16,185,129,0.3)]' : 'bg-black border border-emerald-900/30 rounded-r-lg rounded-tl-lg'} p-2`} data-id="element-2205">
          {/* Metin içerik (varsa) */}
          {message.content && <div className="px-2 py-1" data-id="element-2206">
              <p className="text-sm" data-id="element-2207">{message.content}</p>
            </div>}
          {/* Görsel ve dosya ekler */}
          {message.attachments && message.attachments.length > 0 && <div className="space-y-2" data-id="element-2208">
              {message.attachments.map(attachment => {
            if (attachment.type === 'image') {
              return <div key={attachment.id} className="relative rounded-lg overflow-hidden group cursor-pointer" data-id="element-2209">
                      <img src={attachment.url} alt={attachment.name} className="w-full h-auto max-h-[300px] object-cover hover:scale-105 transition-transform" onClick={() => openImageModal(attachment.url)} data-id="element-2210" />
                      {/* Görsel üzerine bilgi overlay */}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2211">
                        <div className="flex justify-between items-center" data-id="element-2212">
                          <span className="truncate" data-id="element-2213">{attachment.name}</span>
                          <button onClick={e => {
                      e.stopPropagation();
                      downloadAttachment(attachment);
                    }} className="p-1 hover:bg-white/20 rounded" data-id="element-2214">
                            <Download className="w-3 h-3" data-id="element-2215" />
                          </button>
                        </div>
                      </div>
                    </div>;
            } else if (attachment.type === 'document') {
              return <div key={attachment.id} className="flex items-center gap-2 p-3 bg-black/30 rounded-lg border border-emerald-900/30 hover:bg-black/50 transition-colors cursor-pointer" data-id="element-2216">
                      <FileText className="w-5 h-5 text-blue-400" data-id="element-2217" />
                      <div className="flex-1 min-w-0" data-id="element-2218">
                        <p className="text-sm font-medium truncate" data-id="element-2219">
                          {attachment.name}
                        </p>
                        {attachment.size && <p className="text-xs text-gray-400" data-id="element-2220">
                            {attachment.size}
                          </p>}
                      </div>
                      <button onClick={() => downloadAttachment(attachment)} className="p-1 hover:bg-white/10 rounded-full text-gray-400 hover:text-white transition-colors" data-id="element-2221">
                        <Download className="w-4 h-4" data-id="element-2222" />
                      </button>
                    </div>;
            }
            return null;
          })}
            </div>}
          {/* Zaman damgası */}
          <div className={`text-xs mt-2 px-2 ${isCurrentUser ? 'text-white/70' : 'text-gray-400'}`} data-id="element-2223">
            {message.timestamp}
          </div>
        </div>
      </div>
      {/* Görsel modalı */}
      {showImageModal && <ImageModal imageUrl={selectedImageUrl} onClose={() => setShowImageModal(false)} data-id="element-2224" />}
    </>;
}
// System Notification Bubble Component
function SystemNotificationBubble({
  message
}: {
  message: TeacherMessage;
}) {
  const getNotificationStyle = (content: string) => {
    if (content.includes('🎉')) return 'from-green-600 to-emerald-600'; // Güncelleme
    if (content.includes('⚠️')) return 'from-yellow-600 to-orange-600'; // Uyarı
    if (content.includes('📊')) return 'from-blue-600 to-cyan-600'; // Yeni özellik
    if (content.includes('🔔')) return 'from-purple-600 to-indigo-600'; // Hatırlatma
    return 'from-gray-600 to-gray-700'; // Varsayılan
  };
  const getNotificationIcon = (content: string) => {
    if (content.includes('🎉')) return <Check className="w-4 h-4" data-id="element-2225" />;
    if (content.includes('⚠️')) return <AlertTriangle className="w-4 h-4" data-id="element-2226" />;
    if (content.includes('📊')) return <BarChart className="w-4 h-4" data-id="element-2227" />;
    if (content.includes('🔔')) return <Bell className="w-4 h-4" data-id="element-2228" />;
    return <Info className="w-4 h-4" data-id="element-2229" />;
  };
  return <div className="flex justify-center mb-4" data-id="element-2230">
      <div className={`relative max-w-[85%] bg-gradient-to-r ${getNotificationStyle(message.content)} rounded-lg shadow-[0_0_10px_rgba(0,0,0,0.3)] p-4`} data-id="element-2231">
        {/* Sistem bildirimi header */}
        <div className="flex items-center gap-2 mb-3 pb-2 border-b border-white/20" data-id="element-2232">
          <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-white" data-id="element-2233">
            {getNotificationIcon(message.content)}
          </div>
          <span className="text-sm font-medium text-white" data-id="element-2234">
            Sistem Bildirimi
          </span>
          <div className="ml-auto text-xs text-white/70" data-id="element-2235">
            {message.timestamp}
          </div>
        </div>
        {/* Bildirim içeriği */}
        <div className="text-sm text-white leading-relaxed" data-id="element-2236">
          {message.content}
        </div>
        {/* Eğer önemli bir bildirim ise aksiyon butonları */}
        {message.content.includes('Yeni Özellik') && <div className="mt-3 pt-3 border-t border-white/20" data-id="element-2237">
            <button className="px-3 py-1.5 text-xs bg-white/20 hover:bg-white/30 rounded-lg text-white transition-colors" data-id="element-2238">
              Özelliği Keşfet
            </button>
          </div>}
      </div>
    </div>;
}
// Support Message Bubble Component
function SupportMessageBubble({
  message
}: {
  message: TeacherMessage;
}) {
  const isCurrentUser = message.sender === 'teacher';
  const isSupport = message.sender === 'admin';
  const isSystem = message.sender === 'system';
  if (isSystem) {
    return <div className="flex justify-center my-4" data-id="element-2239">
        <div className="px-4 py-2 bg-blue-900/20 rounded-lg text-blue-400 text-sm max-w-[80%] border border-blue-900/30 flex items-center gap-2" data-id="element-2240">
          <Info className="w-4 h-4" data-id="element-2241" />
          {message.content}
        </div>
      </div>;
  }
  return <div className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} mb-4`} data-id="element-2242">
      <div className={`relative max-w-[80%] ${isCurrentUser ? 'bg-gradient-to-r from-emerald-600 to-teal-600 rounded-l-lg rounded-tr-lg shadow-[0_0_10px_rgba(16,185,129,0.3)]' : isSupport ? 'bg-gradient-to-r from-blue-600 to-blue-700 rounded-r-lg rounded-tl-lg shadow-[0_0_10px_rgba(59,130,246,0.3)]' : 'bg-black border border-emerald-900/30 rounded-r-lg rounded-tl-lg'} px-4 py-3`} data-id="element-2243">
        {/* Destek ekibi etiketi */}
        {isSupport && <div className="flex items-center gap-2 mb-2" data-id="element-2244">
            <div className="w-5 h-5 rounded-full bg-blue-400 flex items-center justify-center" data-id="element-2245">
              <Shield className="w-3 h-3 text-white" data-id="element-2246" />
            </div>
            <span className="text-xs font-medium text-blue-200" data-id="element-2247">
              Teknik Destek Ekibi
            </span>
          </div>}
        <p className="text-sm" data-id="element-2248">{message.content}</p>
        {/* Attachments */}
        {message.attachments && message.attachments.length > 0 && <div className="mt-2" data-id="element-2249">
            {message.attachments.map(attachment => {
          if (attachment.type === 'image') {
            return <div key={attachment.id} className="relative rounded-lg overflow-hidden mt-2" data-id="element-2250">
                    <img src={attachment.url} alt={attachment.name} className="w-full h-auto max-h-[200px] object-cover" data-id="element-2251" />
                  </div>;
          } else {
            return <div key={attachment.id} className="flex items-center gap-2 p-2 mt-2 bg-black/30 rounded-lg border border-blue-900/30" data-id="element-2252">
                    <FileText className="w-4 h-4 text-blue-400" data-id="element-2253" />
                    <span className="text-xs" data-id="element-2254">{attachment.name}</span>
                    <a href={attachment.url} download className="ml-auto text-blue-400 hover:text-blue-300" data-id="element-2255">
                      <Download className="w-3 h-3" data-id="element-2256" />
                    </a>
                  </div>;
          }
        })}
          </div>}
        <div className={`text-xs mt-2 ${isCurrentUser ? 'text-white/70' : isSupport ? 'text-blue-200/70' : 'text-gray-400'}`} data-id="element-2257">
          {message.timestamp}
        </div>
      </div>
    </div>;
}
// Teacher Conversation List Item Component
function TeacherConversationListItem({
  conversation,
  isActive,
  onClick
}: {
  conversation: TeacherConversation;
  isActive: boolean;
  onClick: () => void;
}) {
  // Determine icon based on conversation type
  const getIcon = () => {
    switch (conversation.type) {
      case 'student':
        return GraduationCap;
      case 'parent':
        return Users;
      case 'teacher':
        return User;
      case 'admin':
        return Shield;
      case 'support':
        return Info;
      case 'class_group':
        return MessageSquare;
      default:
        return MessageSquare;
    }
  };
  const Icon = getIcon();
  // Special styling for system notifications
  const isSystemNotification = conversation.recipient.id === 'system';
  const isTechnicalSupport = conversation.recipient.id === 'support';
  return <div onClick={onClick} className={`p-3 rounded-lg cursor-pointer transition-colors ${isActive ? isSystemNotification ? 'bg-gradient-to-r from-blue-900/40 to-indigo-900/40 border border-blue-500/30' : isTechnicalSupport ? 'bg-gradient-to-r from-blue-900/40 to-blue-800/40 border border-blue-500/30' : 'bg-gradient-to-r from-emerald-900/40 to-teal-900/40 border border-emerald-500/30' : 'hover:bg-emerald-900/20 border border-transparent'}`} data-id="element-2258">
      <div className="flex items-center gap-3" data-id="element-2259">
        <div className="relative" data-id="element-2260">
          {conversation.recipient.avatar ? <img src={conversation.recipient.avatar} alt={conversation.recipient.name} className="w-10 h-10 rounded-full object-cover" data-id="element-2261" /> : <div className={`w-10 h-10 rounded-full ${isSystemNotification ? 'bg-gradient-to-r from-blue-600 to-indigo-600' : isTechnicalSupport ? 'bg-gradient-to-r from-blue-600 to-blue-700' : 'bg-gradient-to-r from-emerald-600 to-teal-600'} flex items-center justify-center`} data-id="element-2262">
              <Icon className="w-5 h-5 text-white" data-id="element-2263" />
            </div>}
          {/* Unread message indicator */}
          {conversation.unread && <div className={`absolute -left-1 top-0 w-3 h-3 ${isSystemNotification || isTechnicalSupport ? 'bg-blue-500 shadow-[0_0_5px_rgba(59,130,246,0.7)]' : 'bg-emerald-500 shadow-[0_0_5px_rgba(16,185,129,0.7)]'} rounded-full`} data-id="element-2264" />}
          {/* Urgent status indicator */}
          {conversation.status === 'urgent' && <div className="absolute -right-1 -top-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center" data-id="element-2265">
              <AlertTriangle className="w-2 h-2 text-white" data-id="element-2266" />
            </div>}
        </div>
        <div className="flex-1 min-w-0" data-id="element-2267">
          <div className="flex justify-between items-center" data-id="element-2268">
            <h3 className={`text-sm truncate ${conversation.unread ? 'font-bold' : 'font-medium'} ${isSystemNotification || isTechnicalSupport ? 'text-blue-300' : ''}`} data-id="element-2269">
              {conversation.title}
            </h3>
            <span className="text-xs text-gray-400 whitespace-nowrap ml-2" data-id="element-2270">
              {conversation.date}
            </span>
          </div>
          <div className="flex items-center gap-1 mt-1" data-id="element-2271">
            {/* Class/Role information */}
            {conversation.recipient.className && <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-900/30 text-emerald-400" data-id="element-2272">
                {conversation.recipient.className}
              </span>}
            {conversation.recipient.subject && <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-900/30 text-emerald-400" data-id="element-2273">
                {conversation.recipient.subject}
              </span>}
            <p className={`text-xs text-gray-400 truncate ${conversation.unread ? 'font-bold' : 'font-normal'}`} data-id="element-2274">
              {conversation.preview}
            </p>
          </div>
        </div>
      </div>
    </div>;
}
// Conditional Message Composer Component
function ConditionalMessageComposer({
  conversation,
  onSendMessage
}: {
  conversation: TeacherConversation;
  onSendMessage: (content: string, attachments?: File[]) => void;
}) {
  const isSystemNotification = conversation.recipient.id === 'system';
  const isTechnicalSupport = conversation.recipient.id === 'support';
  if (isSystemNotification) {
    // Sistem bildirimleri - sadece okuma
    return <div className="border-t border-emerald-900/30 p-4" data-id="element-2275">
        <div className="flex items-center gap-3 p-4 bg-gray-900/50 rounded-lg border border-gray-700" data-id="element-2276">
          <Info className="w-5 h-5 text-blue-400" data-id="element-2277" />
          <div data-id="element-2278">
            <p className="text-sm font-medium text-white" data-id="element-2279">
              Bu bir sistem bildirimi konuşmasıdır
            </p>
            <p className="text-xs text-gray-400" data-id="element-2280">
              Sistem bildirimlerine yanıt veremezsiniz. Sadece okuyabilirsiniz.
            </p>
          </div>
        </div>
      </div>;
  }
  if (isTechnicalSupport) {
    // Teknik destek - normal mesaj yazma + özel placeholder
    return <TeacherMessageComposer onSendMessage={onSendMessage} disabled={false} placeholder="Teknik sorununuzu detaylı bir şekilde açıklayın..." supportMode={true} data-id="element-2281" />;
  }
  // Normal konuşmalar
  return <TeacherMessageComposer onSendMessage={onSendMessage} disabled={conversation.status === 'archived'} data-id="element-2282" />;
}
// Enhanced Teacher Message Composer Component
function TeacherMessageComposer({
  onSendMessage,
  disabled = false,
  placeholder = 'Mesajınızı yazın...',
  supportMode = false
}: {
  onSendMessage: (content: string, attachments?: File[]) => void;
  disabled?: boolean;
  placeholder?: string;
  supportMode?: boolean;
}) {
  const [message, setMessage] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleSend = () => {
    if (message.trim() || attachments.length > 0) {
      onSendMessage(message, attachments.length > 0 ? attachments : undefined);
      setMessage('');
      setAttachments([]);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  return <div className="border-t border-emerald-900/30 p-4" data-id="element-2283">
      {/* Teknik destek için özel yardım mesajı */}
      {supportMode && <div className="mb-3 p-3 bg-blue-900/20 rounded-lg border border-blue-500/30" data-id="element-2284">
          <div className="flex items-center gap-2 mb-2" data-id="element-2285">
            <Shield className="w-4 h-4 text-blue-400" data-id="element-2286" />
            <span className="text-sm font-medium text-blue-400" data-id="element-2287">
              Teknik Destek İpuçları
            </span>
          </div>
          <ul className="text-xs text-blue-300 space-y-1" data-id="element-2288">
            <li data-id="element-2289">• Sorununuzu detaylı olarak açıklayın</li>
            <li data-id="element-2290">• Hangi tarayıcı kullandığınızı belirtin</li>
            <li data-id="element-2291">• Ekran görüntüsü ekleyebilirsiniz</li>
            <li data-id="element-2292">• Hata mesajı varsa tam olarak yazın</li>
          </ul>
        </div>}
      <TeacherImageAttachment attachments={attachments} setAttachments={setAttachments} fileInputRef={fileInputRef} data-id="element-2293" />
      <div className="relative" data-id="element-2294">
        <textarea value={message} onChange={e => setMessage(e.target.value)} onKeyDown={handleKeyDown} placeholder={disabled ? 'Bu konuşmaya mesaj gönderemezsiniz' : placeholder} disabled={disabled} className="w-full bg-black text-white border border-emerald-900/30 rounded-lg pl-4 pr-24 py-3 min-h-[80px] resize-none focus:outline-none focus:ring-1 focus:ring-emerald-500/50 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2295" />
        <div className="absolute bottom-3 right-3 flex items-center gap-2" data-id="element-2296">
          <button onClick={handleFileSelect} className="p-2 rounded-full bg-black hover:bg-emerald-900/20 text-gray-400 hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-emerald-900/50" disabled={disabled} title="Dosya veya görsel ekle" data-id="element-2297">
            <Paperclip className="w-5 h-5" data-id="element-2298" />
          </button>
          <button onClick={handleSend} disabled={!message.trim() && attachments.length === 0 || disabled} className="p-2 rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2299">
            <Send className="w-5 h-5" data-id="element-2300" />
          </button>
        </div>
      </div>
    </div>;
}
// Teacher Message Bubble Component
function TeacherMessageBubble({
  message
}: {
  message: TeacherMessage;
}) {
  const isCurrentUser = message.sender === 'teacher';
  const isSystem = message.sender === 'system';
  const isSpecialConversation = message.sender === 'system' || message.sender === 'admin';
  // System messages
  if (isSystem) {
    if (message.content.includes('🎉') || message.content.includes('⚠️') || message.content.includes('📊') || message.content.includes('🔔')) {
      return <SystemNotificationBubble message={message} data-id="element-2301" />;
    }
    return <div className="flex justify-center my-4" data-id="element-2302">
        <div className="px-4 py-2 bg-emerald-900/20 rounded-lg text-gray-400 text-sm max-w-[80%] border border-emerald-900/30" data-id="element-2303">
          {message.content}
        </div>
      </div>;
  }
  // Support messages
  if (isSpecialConversation) {
    return <SupportMessageBubble message={message} data-id="element-2304" />;
  }
  // Messages with attachments
  if (message.attachments && message.attachments.length > 0) {
    return <ImageMessageBubble message={message} isCurrentUser={isCurrentUser} data-id="element-2305" />;
  }
  // Regular message
  return <div className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'} mb-4`} data-id="element-2306">
      <div className={`relative max-w-[80%] ${isCurrentUser ? 'bg-gradient-to-r from-emerald-600 to-teal-600 rounded-l-lg rounded-tr-lg shadow-[0_0_10px_rgba(16,185,129,0.3)]' : 'bg-black border border-emerald-900/30 rounded-r-lg rounded-tl-lg'} px-4 py-3`} data-id="element-2307">
        {message.content && <p className="text-sm" data-id="element-2308">{message.content}</p>}
        <div className={`text-xs mt-1 ${isCurrentUser ? 'text-white/70' : 'text-gray-400'}`} data-id="element-2309">
          {message.timestamp}
        </div>
      </div>
    </div>;
}
// Teacher New Message Modal Component
function TeacherNewMessageModal({
  onClose,
  onCreateConversation
}: {
  onClose: () => void;
  onCreateConversation: (recipient: any, message: string) => void;
}) {
  const [selectedRecipient, setSelectedRecipient] = useState<any>(null);
  const [message, setMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  // Add technical support to the recipient list if not already included
  const allRecipients = [...teacherRecipients];
  if (!teacherRecipients.some(r => r.id === 'support')) {
    allRecipients.push({
      id: 'support',
      name: 'Teknik Destek',
      role: 'Yardım Masası',
      avatar: null,
      type: 'support'
    });
  }
  // Filter recipients by type and search query
  const filteredRecipients = allRecipients.filter(recipient => {
    const matchesSearch = recipient.name.toLowerCase().includes(searchQuery.toLowerCase()) || recipient.role && recipient.role.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = selectedType === 'all' || recipient.type === selectedType;
    return matchesSearch && matchesType;
  });
  const recipientTypes = [{
    value: 'all',
    label: 'Tümü'
  }, {
    value: 'student',
    label: 'Öğrenciler'
  }, {
    value: 'parent',
    label: 'Veliler'
  }, {
    value: 'teacher',
    label: 'Öğretmenler'
  }, {
    value: 'admin',
    label: 'Yönetim'
  }, {
    value: 'support',
    label: 'Destek'
  }];
  const handleCreateConversation = () => {
    if (selectedRecipient && message.trim()) {
      onCreateConversation(selectedRecipient, message);
      onClose();
    }
  };
  return <div className="fixed inset-0 bg-black/90 flex items-center justify-center p-4 z-50" data-id="element-2310">
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-600 shadow-[0_0_20px_rgba(16,185,129,0.4)] w-full max-w-xl" data-id="element-2311">
        <div className="relative bg-black rounded-xl" data-id="element-2312">
          <div className="p-4 border-b border-emerald-900/30 flex justify-between items-center" data-id="element-2313">
            <h2 className="text-xl font-bold" data-id="element-2314">Yeni Mesaj</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white" data-id="element-2315">
              <X className="w-6 h-6" data-id="element-2316" />
            </button>
          </div>
          <div className="p-6 space-y-4" data-id="element-2317">
            {/* Recipient type selection */}
            <div className="space-y-2" data-id="element-2318">
              <label className="text-sm font-medium text-gray-300" data-id="element-2319">
                Alıcı Türü
              </label>
              <div className="flex flex-wrap gap-2" data-id="element-2320">
                {recipientTypes.map(type => <button key={type.value} onClick={() => setSelectedType(type.value)} className={`px-3 py-1.5 rounded-lg text-xs transition-colors ${selectedType === type.value ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white' : 'bg-emerald-900/20 text-emerald-400 hover:bg-emerald-900/30'}`} data-id="element-2321">
                    {type.label}
                  </button>)}
              </div>
            </div>
            {/* Search */}
            <div className="space-y-2" data-id="element-2322">
              <label className="text-sm font-medium text-gray-300" data-id="element-2323">Alıcı</label>
              <div className="relative" data-id="element-2324">
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="İsim veya rol ara..." className="w-full bg-black border border-emerald-900/30 text-white rounded-lg pl-10 pr-4 py-3 focus:outline-none focus:ring-1 focus:ring-emerald-500/50" data-id="element-2325" />
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-2326" />
              </div>
            </div>
            {/* Recipient list */}
            <div className="space-y-2 max-h-[200px] overflow-y-auto" data-id="element-2327">
              {filteredRecipients.map(recipient => <div key={recipient.id} onClick={() => setSelectedRecipient(recipient)} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer ${selectedRecipient?.id === recipient.id ? 'bg-gradient-to-r from-emerald-900/40 to-teal-900/40 border border-emerald-500/30' : 'hover:bg-emerald-900/20 border border-transparent'}`} data-id="element-2328">
                  {recipient.avatar ? <img src={recipient.avatar} alt={recipient.name} className="w-10 h-10 rounded-full object-cover" data-id="element-2329" /> : <div className={`w-10 h-10 rounded-full ${recipient.type === 'support' ? 'bg-gradient-to-r from-blue-600 to-blue-700' : 'bg-gradient-to-r from-emerald-600 to-teal-600'} flex items-center justify-center`} data-id="element-2330">
                      {recipient.type === 'support' ? <Shield className="w-5 h-5 text-white" data-id="element-2331" /> : <User className="w-5 h-5 text-white" data-id="element-2332" />}
                    </div>}
                  <div className="flex-1" data-id="element-2333">
                    <h3 className="font-medium" data-id="element-2334">{recipient.name}</h3>
                    <p className="text-sm text-gray-400" data-id="element-2335">{recipient.role}</p>
                    {recipient.className && <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-900/30 text-emerald-400" data-id="element-2336">
                        {recipient.className}
                      </span>}
                  </div>
                  {selectedRecipient?.id === recipient.id && <Check className="ml-auto w-5 h-5 text-emerald-400" data-id="element-2337" />}
                </div>)}
              {filteredRecipients.length === 0 && <div className="text-center py-4 text-gray-400" data-id="element-2338">
                  Aranan kişi bulunamadı
                </div>}
            </div>
            {/* Message */}
            <div className="space-y-2" data-id="element-2339">
              <label className="text-sm font-medium text-gray-300" data-id="element-2340">Mesaj</label>
              <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Mesajınızı yazın..." className="w-full bg-black border border-emerald-900/30 text-white rounded-lg px-4 py-3 min-h-[100px] resize-none focus:outline-none focus:ring-1 focus:ring-emerald-500/50" data-id="element-2341" />
            </div>
          </div>
          <div className="p-4 border-t border-emerald-900/30 flex justify-end gap-3" data-id="element-2342">
            <button onClick={onClose} className="px-4 py-2 bg-transparent border border-emerald-900/30 hover:bg-emerald-900/10 text-gray-300 rounded-lg transition-colors" data-id="element-2343">
              İptal
            </button>
            <button onClick={handleCreateConversation} disabled={!selectedRecipient || !message.trim()} className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2344">
              Gönder
            </button>
          </div>
        </div>
      </div>
    </div>;
}
// Main Teacher Messaging Component
const TeacherMessaging = () => {
  const [conversations, setConversations] = useState<TeacherConversation[]>(sampleConversations);
  const [activeConversation, setActiveConversation] = useState<TeacherConversation | null>(null);
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Ensure system and technical support conversations are included
  useEffect(() => {
    // Check if system notification conversation exists
    const systemNotificationExists = conversations.some(conv => conv.recipient.id === 'system');
    // Check if technical support conversation exists
    const technicalSupportExists = conversations.some(conv => conv.recipient.id === 'support');
    if (!systemNotificationExists || !technicalSupportExists) {
      let updatedConversations = [...conversations];
      // Add system notifications if not exists
      if (!systemNotificationExists) {
        updatedConversations = [systemNotificationConversation, ...updatedConversations];
      }
      // Add technical support if not exists
      if (!technicalSupportExists) {
        updatedConversations = [technicalSupportConversation, ...updatedConversations];
      }
      setConversations(updatedConversations);
    }
  }, []);
  // Filter conversations based on search and type
  const filteredConversations = conversations.filter(conversation => {
    const matchesSearch = conversation.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || conversation.type === filterType || filterType === 'urgent' && conversation.status === 'urgent';
    return matchesSearch && matchesType;
  });
  // Set first conversation as active by default
  useEffect(() => {
    if (conversations.length > 0 && !activeConversation) {
      setActiveConversation(conversations[0]);
    }
  }, [conversations, activeConversation]);
  // Scroll to bottom of messages when conversation changes
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [activeConversation]);
  // Handle sending a message
  const handleSendMessage = (content: string, attachments?: File[]) => {
    if (!activeConversation) return;
    // Create new message with attachments if provided
    const newMessage: TeacherMessage = {
      id: `msg-${Date.now()}`,
      sender: 'teacher',
      content,
      timestamp: new Date().toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit'
      }),
      attachments: attachments ? attachments.map(file => ({
        id: `attachment-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: file.name,
        type: file.type.startsWith('image/') ? 'image' : file.type.startsWith('video/') ? 'video' : 'document',
        url: URL.createObjectURL(file),
        size: `${(file.size / 1024).toFixed(1)} KB`
      })) : undefined
    };
    // Update conversation with new message
    const updatedConversation = {
      ...activeConversation,
      messages: [...activeConversation.messages, newMessage],
      preview: content || 'Dosya gönderildi',
      date: 'Şimdi',
      unread: false
    };
    // Update conversations list
    setConversations(conversations.map(conv => conv.id === activeConversation.id ? updatedConversation : conv));
    // Update active conversation
    setActiveConversation(updatedConversation);
  };
  // Handle creating a new conversation
  const handleCreateConversation = (recipient: any, message: string) => {
    // Check if conversation already exists
    const existingConversation = conversations.find(conv => conv.recipient.id === recipient.id);
    if (existingConversation) {
      // Add message to existing conversation
      const newMessage: TeacherMessage = {
        id: `msg-${Date.now()}`,
        sender: 'teacher',
        content: message,
        timestamp: new Date().toLocaleTimeString('tr-TR', {
          hour: '2-digit',
          minute: '2-digit'
        })
      };
      const updatedConversation = {
        ...existingConversation,
        messages: [...existingConversation.messages, newMessage],
        preview: message,
        date: 'Şimdi',
        unread: false
      };
      // Update conversations
      setConversations(conversations.map(conv => conv.id === existingConversation.id ? updatedConversation : conv));
      // Set as active conversation
      setActiveConversation(updatedConversation);
    } else {
      // Create new conversation
      const newConversation: TeacherConversation = {
        id: `conv-${Date.now()}`,
        type: recipient.type,
        title: recipient.name,
        preview: message,
        status: 'active',
        date: 'Şimdi',
        unread: false,
        recipient: recipient,
        messages: [{
          id: `msg-${Date.now()}`,
          sender: 'teacher',
          content: message,
          timestamp: new Date().toLocaleTimeString('tr-TR', {
            hour: '2-digit',
            minute: '2-digit'
          })
        }]
      };
      // Add to conversations
      setConversations([newConversation, ...conversations]);
      // Set as active conversation
      setActiveConversation(newConversation);
    }
  };
  // Handle marking a conversation as read
  const handleConversationClick = (conversation: TeacherConversation) => {
    // If already unread, update to read
    if (conversation.unread) {
      const updatedConversation = {
        ...conversation,
        unread: false
      };
      // Update conversations
      setConversations(conversations.map(conv => conv.id === conversation.id ? updatedConversation : conv));
      // Set as active conversation
      setActiveConversation(updatedConversation);
    } else {
      // Just set as active
      setActiveConversation(conversation);
    }
  };
  // Filter options
  const filterOptions = [{
    value: 'all',
    label: 'Tümü'
  }, {
    value: 'student',
    label: 'Öğrenciler'
  }, {
    value: 'parent',
    label: 'Veliler'
  }, {
    value: 'teacher',
    label: 'Öğretmenler'
  }, {
    value: 'admin',
    label: 'Yönetim'
  }, {
    value: 'support',
    label: 'Destek'
  }, {
    value: 'urgent',
    label: 'Acil'
  }];
  return <div className="bg-black min-h-screen" data-id="element-2345">
      <div className="container mx-auto p-4 md:p-6" data-id="element-2346">
        <h1 className="text-2xl font-bold mb-6" data-id="element-2347">Mesajlar</h1>
        <div className="p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-emerald-600 to-teal-600" data-id="element-2348">
          <div className="bg-black/90 rounded-xl min-h-[calc(100vh-140px)]" data-id="element-2349">
            <div className="grid grid-cols-1 md:grid-cols-3 h-full" data-id="element-2350">
              {/* Conversations List */}
              <div className="md:col-span-1 border-r border-emerald-900/30" data-id="element-2351">
                <div className="p-4 border-b border-emerald-900/30" data-id="element-2352">
                  <div className="flex items-center justify-between mb-4" data-id="element-2353">
                    <h2 className="text-lg font-medium" data-id="element-2354">Konuşmalar</h2>
                    <button onClick={() => setShowNewMessageModal(true)} className="p-2 rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white hover:opacity-90 transition-colors" data-id="element-2355">
                      <Plus className="w-5 h-5" data-id="element-2356" />
                    </button>
                  </div>
                  <div className="flex gap-2" data-id="element-2357">
                    <div className="relative flex-1" data-id="element-2358">
                      <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Ara..." className="w-full bg-black border border-emerald-900/30 text-white rounded-lg pl-10 pr-4 py-2.5 focus:outline-none focus:ring-1 focus:ring-emerald-500/50" data-id="element-2359" />
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-2360" />
                    </div>
                    <div className="relative" data-id="element-2361">
                      <button onClick={() => setShowFilterDropdown(!showFilterDropdown)} className="p-2.5 rounded-lg bg-black border border-emerald-900/30 hover:bg-emerald-900/20 transition-colors text-gray-400" data-id="element-2362">
                        <Filter className="w-5 h-5" data-id="element-2363" />
                      </button>
                      {showFilterDropdown && <div className="absolute right-0 top-full mt-1 z-10 w-48 bg-gray-900 border border-emerald-900/30 rounded-lg shadow-lg py-1 overflow-hidden" data-id="element-2364">
                          {filterOptions.map(option => <button key={option.value} onClick={() => {
                        setFilterType(option.value);
                        setShowFilterDropdown(false);
                      }} className={`w-full text-left px-4 py-2 ${filterType === option.value ? 'bg-emerald-900/30 text-emerald-400' : 'hover:bg-emerald-900/20 text-white'}`} data-id="element-2365">
                              {option.label}
                            </button>)}
                        </div>}
                    </div>
                  </div>
                </div>
                <div className="overflow-y-auto h-[calc(100vh-240px)] p-2" data-id="element-2366">
                  {filteredConversations.length > 0 ? <div className="space-y-2" data-id="element-2367">
                      {filteredConversations.map(conversation => <TeacherConversationListItem key={conversation.id} conversation={conversation} isActive={activeConversation ? activeConversation.id === conversation.id : false} onClick={() => handleConversationClick(conversation)} data-id="element-2368" />)}
                    </div> : <div className="text-center py-10 text-gray-400" data-id="element-2369">
                      {searchQuery || filterType !== 'all' ? 'Arama kriterlerine uygun konuşma bulunamadı' : 'Henüz bir konuşma başlatmadınız'}
                    </div>}
                </div>
              </div>
              {/* Message Area */}
              <div className="md:col-span-2 flex flex-col h-full" data-id="element-2370">
                {activeConversation ? <>
                    {/* Conversation Header */}
                    <div className="p-4 border-b border-emerald-900/30 flex justify-between items-center" data-id="element-2371">
                      <div className="flex items-center gap-3" data-id="element-2372">
                        {activeConversation.recipient.avatar ? <img src={activeConversation.recipient.avatar} alt={activeConversation.recipient.name} className="w-10 h-10 rounded-full object-cover" data-id="element-2373" /> : <div className={`w-10 h-10 rounded-full ${activeConversation.recipient.id === 'system' ? 'bg-gradient-to-r from-blue-600 to-indigo-600' : activeConversation.recipient.id === 'support' ? 'bg-gradient-to-r from-blue-600 to-blue-700' : 'bg-gradient-to-r from-emerald-600 to-teal-600'} flex items-center justify-center`} data-id="element-2374">
                            <User className="w-5 h-5 text-white" data-id="element-2375" />
                          </div>}
                        <div data-id="element-2376">
                          <h3 className="font-medium" data-id="element-2377">
                            {activeConversation.title}
                          </h3>
                          <div className="flex items-center gap-2" data-id="element-2378">
                            <p className="text-sm text-gray-400" data-id="element-2379">
                              {activeConversation.recipient.role}
                            </p>
                            {activeConversation.recipient.className && <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-900/30 text-emerald-400" data-id="element-2380">
                                {activeConversation.recipient.className}
                              </span>}
                          </div>
                        </div>
                      </div>
                      <button className="p-2 rounded-full hover:bg-emerald-900/20 text-gray-400 hover:text-white transition-colors" data-id="element-2381">
                        <MoreVertical className="w-5 h-5" data-id="element-2382" />
                      </button>
                    </div>
                    {/* Messages */}
                    <div className="flex-1 p-4 overflow-y-auto" data-id="element-2383">
                      {activeConversation.messages.map(message => <TeacherMessageBubble key={message.id} message={message} data-id="element-2384" />)}
                      <div ref={messagesEndRef} data-id="element-2385" />
                    </div>
                    {/* Message Composer - Conditional based on conversation type */}
                    <ConditionalMessageComposer conversation={activeConversation} onSendMessage={handleSendMessage} data-id="element-2386" />
                  </> : <div className="flex-1 flex flex-col items-center justify-center p-4 text-center text-gray-400" data-id="element-2387">
                    <MessageSquare className="w-16 h-16 mb-4 opacity-20" data-id="element-2388" />
                    <h3 className="text-xl font-medium mb-2" data-id="element-2389">Mesajlarınız</h3>
                    <p className="max-w-md mb-6" data-id="element-2390">
                      Öğrenciler, veliler ve diğer öğretmenlerle iletişim kurun.
                      Yeni bir konuşma başlatmak için sol üstteki + butonuna
                      tıklayın.
                    </p>
                    <button onClick={() => setShowNewMessageModal(true)} className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-lg hover:opacity-90 transition-colors" data-id="element-2391">
                      Yeni Mesaj
                    </button>
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* New Message Modal */}
      {showNewMessageModal && <TeacherNewMessageModal onClose={() => setShowNewMessageModal(false)} onCreateConversation={handleCreateConversation} data-id="element-2392" />}
    </div>;
};
export default TeacherMessaging;